import { useState } from 'react';
import * as Icons from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function LineItemsEditor({ value, onChange }) {
  const items = value || [];
  const update = (idx, patch) => {
    const next = items.map((it, i) => i === idx ? { ...it, ...patch } : it);
    onChange(next);
  };
  const add = () => onChange([...items, { description: '', quantity: 1, rate: 0 }]);
  const remove = (idx) => onChange(items.filter((_, i) => i !== idx));

  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`line-item-${i}`}>
          <input
            className="col-span-6 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-600 focus:border-transparent outline-none"
            placeholder="Description"
            value={it.description}
            onChange={(e) => update(i, { description: e.target.value })}
            data-testid={`line-item-desc-${i}`}
          />
          <input
            type="number"
            className="col-span-2 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-600 focus:border-transparent outline-none"
            placeholder="Qty"
            value={it.quantity}
            onChange={(e) => update(i, { quantity: e.target.value === '' ? '' : Number(e.target.value) })}
            data-testid={`line-item-qty-${i}`}
          />
          <input
            type="number"
            className="col-span-3 bg-stone-50 border border-stone-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-600 focus:border-transparent outline-none"
            placeholder="Rate"
            value={it.rate}
            onChange={(e) => update(i, { rate: e.target.value === '' ? '' : Number(e.target.value) })}
            data-testid={`line-item-rate-${i}`}
          />
          <button
            type="button"
            className="col-span-1 text-stone-400 hover:text-red-600 transition-colors grid place-items-center"
            onClick={() => remove(i)}
            data-testid={`line-item-remove-${i}`}
            aria-label="Remove line"
          >
            <Icons.Trash2 size={16} />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        className="w-full text-sm font-medium text-stone-700 border border-dashed border-stone-300 rounded-lg py-2 hover:bg-stone-100 hover:border-stone-400 transition-colors flex items-center justify-center gap-1"
        data-testid="add-line-item"
      >
        <Icons.Plus size={14} /> Add line
      </button>
    </div>
  );
}

export default function FormBuilder({ template, values, setValues }) {
  const [aiBusy, setAiBusy] = useState({});
  const [draftPrompt, setDraftPrompt] = useState('');
  const [draftBusy, setDraftBusy] = useState(false);
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const isPro = !!user?.is_pro;

  const setField = (key, val) => setValues((v) => ({ ...v, [key]: val }));

  const handleProGate = (action) => {
    if (!user) {
      toast.info('Sign in to use AI.');
      login(window.location.pathname);
      return false;
    }
    if (!isPro) {
      toast.error('AI is a Pro feature.', {
        action: { label: 'Upgrade', onClick: () => navigate('/pricing') },
      });
      return false;
    }
    return true;
  };

  const generateField = async (field) => {
    if (!handleProGate()) return;
    const prompt = window.prompt(`What should this field say?\n\nField: ${field.label}\n\nExample: "Write a 2-paragraph confidentiality clause for software consulting."`);
    if (!prompt) return;
    setAiBusy((b) => ({ ...b, [field.key]: true }));
    try {
      const res = await axios.post(`${API}/ai/generate-field`, {
        template_id: template.id,
        field_key: field.key,
        field_label: field.label,
        prompt,
      });
      setField(field.key, res.data.content);
      toast.success('Field generated');
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error('Pro required.', { action: { label: 'Upgrade', onClick: () => navigate('/pricing') } });
      } else {
        toast.error('AI generation failed. Try again.');
      }
    } finally {
      setAiBusy((b) => ({ ...b, [field.key]: false }));
    }
  };

  const draftAll = async () => {
    if (!handleProGate()) return;
    if (!draftPrompt.trim()) {
      toast.error('Tell the AI what you want first.');
      return;
    }
    setDraftBusy(true);
    try {
      const defs = template.fields
        .filter((f) => f.type !== 'lineitems')
        .map((f) => ({ key: f.key, label: f.label, type: f.type }));
      const res = await axios.post(`${API}/ai/draft-document`, {
        template_id: template.id,
        template_title: template.title,
        field_definitions: defs,
        user_prompt: draftPrompt,
      });
      setValues((v) => ({ ...v, ...res.data.values }));
      toast.success('Document drafted by AI');
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error('Pro required.', { action: { label: 'Upgrade', onClick: () => navigate('/pricing') } });
      } else {
        toast.error('AI draft failed.');
      }
    } finally {
      setDraftBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      {/* AI Draft block */}
      <div className="relative bg-stone-900 text-white rounded-2xl p-5 overflow-hidden">
        <div className="absolute -top-12 -right-12 w-40 h-40 bg-orange-500/30 blur-3xl rounded-full" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-3">
            <Icons.Sparkles size={16} className="text-orange-400" />
            <span className="font-display font-medium tracking-tight text-sm uppercase">Draft with AI</span>
            {!isPro && (
              <span className="ml-auto text-[10px] uppercase tracking-wider bg-orange-500/20 text-orange-300 px-2 py-0.5 rounded-full font-bold">Pro</span>
            )}
          </div>
          <Textarea
            value={draftPrompt}
            onChange={(e) => setDraftPrompt(e.target.value)}
            placeholder={`Describe what you need… e.g. "${aiPlaceholder(template)}"`}
            className="bg-stone-800 border-stone-700 text-white placeholder:text-stone-500 min-h-[68px]"
            data-testid="ai-draft-prompt"
          />
          <Button
            onClick={draftAll}
            disabled={draftBusy}
            className="mt-3 bg-orange-600 hover:bg-orange-700 text-white rounded-full px-5 btn-clay"
            data-testid="ai-draft-btn"
          >
            {draftBusy ? <Icons.Loader2 className="animate-spin mr-2" size={14} /> : <Icons.Wand2 size={14} className="mr-2" />}
            {draftBusy ? 'Drafting…' : isPro ? 'Fill the entire form' : 'Try with Pro'}
          </Button>
        </div>
      </div>

      {template.fields.map((field) => (
        <div key={field.key} data-testid={`field-wrap-${field.key}`}>
          <div className="flex items-center justify-between mb-1.5">
            <Label className="text-sm font-medium tracking-tight text-stone-700">
              {field.label}
              {field.required && <span className="text-orange-600 ml-1">*</span>}
            </Label>
            {field.type === 'textarea' && (
              <button
                type="button"
                onClick={() => generateField(field)}
                className="text-xs text-orange-600 hover:text-orange-800 flex items-center gap-1"
                data-testid={`ai-field-${field.key}`}
                disabled={aiBusy[field.key]}
              >
                {aiBusy[field.key] ? <Icons.Loader2 size={12} className="animate-spin" /> : <Icons.Sparkles size={12} />}
                AI{!isPro && <span className="ml-0.5 text-[9px] uppercase">Pro</span>}
              </button>
            )}
          </div>

          {field.type === 'textarea' ? (
            <Textarea
              value={values[field.key] ?? ''}
              onChange={(e) => setField(field.key, e.target.value)}
              placeholder={field.placeholder}
              className="min-h-[90px] bg-stone-50 border-stone-200 focus-visible:ring-orange-600"
              data-testid={`field-${field.key}`}
            />
          ) : field.type === 'lineitems' ? (
            <LineItemsEditor value={values[field.key] ?? []} onChange={(v) => setField(field.key, v)} />
          ) : (
            <Input
              type={field.type === 'number' ? 'number' : field.type === 'date' ? 'date' : 'text'}
              value={values[field.key] ?? ''}
              onChange={(e) => setField(field.key, field.type === 'number' ? (e.target.value === '' ? '' : Number(e.target.value)) : e.target.value)}
              placeholder={field.placeholder}
              className="bg-stone-50 border-stone-200 focus-visible:ring-orange-600"
              data-testid={`field-${field.key}`}
            />
          )}
        </div>
      ))}
    </div>
  );
}

function aiPlaceholder(template) {
  switch (template.id) {
    case 'nda': return 'Mutual NDA between Acme and Jane Doe for software consulting work, 3-year term, California law.';
    case 'invoice': return 'Bill ClientCo $3,200 for 16 hours of design work at $200/hr, due in 14 days.';
    case 'resume': return 'Senior product designer with 6 years experience at fintech startups, MIT grad.';
    case 'cover-letter': return 'Cover letter for senior engineer role at Stripe, focus on payments experience.';
    case 'offer-letter': return 'Offer Mary Chen the Senior Engineer role at TechCo at $145k starting March 1st.';
    case 'rental-agreement': return '12-month lease for 1BR apartment in SF, $2800/mo, $2800 deposit, no pets.';
    default: return 'Describe the document you need in plain English.';
  }
}
